import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import PreviewIcon from "@mui/icons-material/Preview";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import html2pdf from "html2pdf.js";
import { getEmploymentAgreement } from "../../../Action/api";
import DefaultLogo from "../../../Assets/images/EA-Logo.png";
import "./EmploymentAgreement.css";

const EmploymentAgreementGenerator = ({ open, onClose, personData }) => {
  const [isEditing, setIsEditing] = useState(true);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [agreementSections, setAgreementSections] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(DefaultLogo);
  const [agreementData, setAgreementData] = useState({
    employeeName: "",
    employeeId: "",
    position: "",
    department: "",
    startDate: "",
    salary: "",
    workLocation: "",
    employmentType: "",
    reportingManager: "",
    probationPeriod: "",
    workingHours: "9:00 AM - 6:00 PM",
    annualLeave: "",
    sickLeave: "",
    emergencyLeave: "",
    noticePeriod: "30 days",
    companyName: "Global Command Center",
    companyAddress: "123 Business Street, Chennai, India",
    agreementDate: new Date().toISOString().split("T")[0],
    countryCode: "IN", // Default country code
    employerName: "HR Manager", // Default employer/signatory name
    employerDesignation: "Human Resources Manager",
    employerSignatureDate: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    if (personData) {
      setAgreementData((prev) => ({
        ...prev,
        employeeName: personData.name || "",
        employeeId: personData.uId || personData.staff_id || "",
        position: personData.role_name || personData.desName || "",
        department: personData.dep_name || "",
        startDate: personData.joiningDate || "",
        workLocation: personData.workLocation || "",
        employmentType: personData.empTypeName || "Full-time",
        reportingManager: personData.reportingManagerName || "",
        probationPeriod: personData.probationPeriod || "3 months",
        annualLeave: personData.allocatedAnnualLeave?.toString() || "20",
        sickLeave: personData.allocatedSickLeave?.toString() || "10",
        emergencyLeave: personData.allocatedEmergencyLeave?.toString() || "5",
        countryCode: personData.res_country === "India" ? "IN" : personData.res_country || "IN",
      }));
    }
  }, [personData]);

  // Fetch employment agreement template based on role
  useEffect(() => {
    const fetchAgreementTemplate = async () => {
      if (!personData?.roleId || !open) return;

      setIsLoadingTemplate(true);
      setTemplateError(null);

      try {
        const countryCode = personData.res_country === "India" ? "IN" : "IN"; // Default to IN
        const template = await getEmploymentAgreement(personData.roleId, countryCode);
        
        if (template) {
          setAgreementSections(template);
          console.log("Employment agreement template loaded:", template);
        } else {
          setTemplateError("No agreement template found for this role");
        }
      } catch (error) {
        console.error("Error fetching agreement template:", error);
        setTemplateError("Failed to load agreement template. Using default template.");
      } finally {
        setIsLoadingTemplate(false);
      }
    };

    fetchAgreementTemplate();
  }, [personData?.roleId, open, personData?.res_country]);

  const handleRefreshTemplate = async () => {
    if (!personData?.roleId) return;

    setIsLoadingTemplate(true);
    setTemplateError(null);

    try {
      const template = await getEmploymentAgreement(
        personData.roleId,
        agreementData.countryCode
      );
      
      if (template) {
        setAgreementSections(template);
      } else {
        setTemplateError("No agreement template found");
      }
    } catch (error) {
      setTemplateError("Failed to refresh template");
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const handleChange = (field, value) => {
    setAgreementData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleGeneratePDF = () => {
    const element = document.getElementById("employment-agreement-content");
    const opt = {
      margin: 0.5,
      filename: `Employment_Agreement_${agreementData.employeeId}_${agreementData.employeeName}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCompanyLogo(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setCompanyLogo(DefaultLogo);
  };

  // Generate a signature-like representation from employer name
  const generateSignatureStyle = (name) => {
    if (!name) return "";
    
    // Create a stylized signature with connected letters and flourishes
    const initials = name.split(' ').map(word => word[0]).join('');
    const signature = name.charAt(0).toUpperCase() + 
                     name.slice(1).toLowerCase().split(' ')[0].substring(0, 4) + 
                     "～" + initials;
    return signature;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#1976d2",
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="h6">
            {isEditing ? "Edit Employment Agreement" : "Employment Agreement Preview"}
          </Typography>
          {agreementSections && (
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Template loaded for Role: {personData?.roleId} | Country: {agreementData.countryCode}
            </Typography>
          )}
        </Box>
        <Box>
          {personData?.roleId && (
            <IconButton 
              onClick={handleRefreshTemplate} 
              sx={{ color: "white", mr: 1 }}
              title="Refresh Template"
              disabled={isLoadingTemplate}
            >
              <RefreshIcon />
            </IconButton>
          )}
          <IconButton onClick={toggleEdit} sx={{ color: "white", mr: 1 }}>
            {isEditing ? <PreviewIcon /> : <EditIcon />}
          </IconButton>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Loading State */}
        {isLoadingTemplate && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
            <CircularProgress size={40} />
            <Typography sx={{ ml: 2 }}>Loading agreement template...</Typography>
          </Box>
        )}

        {/* Error Alert */}
        {templateError && (
          <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setTemplateError(null)}>
            {templateError}
          </Alert>
        )}

        {/* Template Info */}
        {agreementSections && !isLoadingTemplate && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Agreement template loaded successfully! Role-specific content is being used.
          </Alert>
        )}

        {isEditing ? (
          // Edit Mode
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: "#1976d2" }}>
                Company Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Company Logo
                </Typography>
                {companyLogo ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <img
                      src={companyLogo}
                      alt="Company Logo"
                      style={{ maxHeight: "80px", maxWidth: "200px", objectFit: "contain" }}
                    />
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      onClick={handleRemoveLogo}
                    >
                      Reset to Default
                    </Button>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                    >
                      Change Logo
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleLogoUpload}
                      />
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                  >
                    Upload Logo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={agreementData.companyName}
                onChange={(e) => handleChange("companyName", e.target.value)}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed'
                  }
                }}
                helperText="Auto-populated (cannot be modified)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Agreement Date"
                type="date"
                value={agreementData.agreementDate}
                onChange={(e) => handleChange("agreementDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Address"
                value={agreementData.companyAddress}
                onChange={(e) => handleChange("companyAddress", e.target.value)}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, color: "#1976d2" }}>
                Employer/Signatory Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Employer/Signatory Name"
                value={agreementData.employerName}
                onChange={(e) => handleChange("employerName", e.target.value)}
                placeholder="e.g., HR Manager, CEO"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Employer Designation"
                value={agreementData.employerDesignation}
                onChange={(e) => handleChange("employerDesignation", e.target.value)}
                placeholder="e.g., Human Resources Manager"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Employer Signature Date"
                type="date"
                value={agreementData.employerSignatureDate}
                onChange={(e) => handleChange("employerSignatureDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {/* Empty grid for spacing */}
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, color: "#1976d2" }}>
                Employee Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Employee Name"
                value={agreementData.employeeName}
                onChange={(e) => handleChange("employeeName", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Employee ID"
                value={agreementData.employeeId}
                onChange={(e) => handleChange("employeeId", e.target.value)}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed'
                  }
                }}
                helperText="Auto-populated (cannot be modified)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Position/Role"
                value={agreementData.position}
                onChange={(e) => handleChange("position", e.target.value)}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed'
                  }
                }}
                helperText="Auto-populated (cannot be modified)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Department"
                value={agreementData.department}
                onChange={(e) => handleChange("department", e.target.value)}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed'
                  }
                }}
                helperText="Auto-populated (cannot be modified)"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, color: "#1976d2" }}>
                Employment Terms
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={agreementData.startDate}
                onChange={(e) => handleChange("startDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Employment Type"
                value={agreementData.employmentType}
                onChange={(e) => handleChange("employmentType", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Salary"
                value={agreementData.salary}
                onChange={(e) => handleChange("salary", e.target.value)}
                placeholder="e.g., $50,000 per annum"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Work Location"
                value={agreementData.workLocation}
                onChange={(e) => handleChange("workLocation", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Reporting Manager"
                value={agreementData.reportingManager}
                onChange={(e) => handleChange("reportingManager", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Probation Period"
                value={agreementData.probationPeriod}
                onChange={(e) => handleChange("probationPeriod", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Working Hours"
                value={agreementData.workingHours}
                onChange={(e) => handleChange("workingHours", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Notice Period"
                value={agreementData.noticePeriod}
                onChange={(e) => handleChange("noticePeriod", e.target.value)}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed'
                  }
                }}
                helperText="Auto-populated (cannot be modified)"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, color: "#1976d2" }}>
                Leave Entitlements
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Annual Leave (days)"
                type="number"
                value={agreementData.annualLeave}
                onChange={(e) => handleChange("annualLeave", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Sick Leave (days)"
                type="number"
                value={agreementData.sickLeave}
                onChange={(e) => handleChange("sickLeave", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Emergency Leave (days)"
                type="number"
                value={agreementData.emergencyLeave}
                onChange={(e) => handleChange("emergencyLeave", e.target.value)}
              />
            </Grid>
          </Grid>
        ) : (
          // Preview Mode - Formatted Agreement
          <Paper
            id="employment-agreement-content"
            elevation={0}
            sx={{ p: 0, backgroundColor: "white", position: "relative" }}
          >
            {/* Header */}
            <Box
              sx={{
                borderBottom: "3px solid #1976d2",
                pb: 2,
                mb: 3,
                px: 4,
                pt: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                {companyLogo && (
                  <img
                    src={companyLogo}
                    alt="Company Logo"
                    style={{ maxHeight: "60px", maxWidth: "180px", objectFit: "contain" }}
                  />
                )}
                {!companyLogo && (
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                    {agreementData.companyName}
                  </Typography>
                )}
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  EMPLOYMENT AGREEMENT
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Date: {new Date(agreementData.agreementDate).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>

            {/* Main Content */}
            <Box sx={{ px: 4 }}>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                  EMPLOYMENT AGREEMENT
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {agreementData.companyName}
                </Typography>
              </Box>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="body1" paragraph sx={{ textAlign: "justify" }}>
              This Employment Agreement ("Agreement") is entered into on{" "}
              <strong>
                {new Date(agreementData.agreementDate).toLocaleDateString(
                  "en-US",
                  {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }
                )}
              </strong>
              , between:
            </Typography>

            <Box sx={{ ml: 4, mb: 3 }}>
              <Typography variant="body1" paragraph>
                <strong>Employer:</strong> {agreementData.companyName}
                <br />
                {agreementData.companyAddress}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Employee:</strong> {agreementData.employeeName}
                <br />
                <strong>Employee ID:</strong> {agreementData.employeeId}
              </Typography>
            </Box>

            {/* Render sections from API if available, otherwise use default */}
            {agreementSections ? (
              <>
                {Object.keys(agreementSections)
                  .filter((key) => key.match(/^\d+\./)) // Filter keys that start with numbers
                  .sort((a, b) => {
                    const numA = parseInt(a.match(/^\d+/)[0]);
                    const numB = parseInt(b.match(/^\d+/)[0]);
                    return numA - numB;
                  })
                  .map((sectionKey) => {
                    const sectionContent = agreementSections[sectionKey];
                    const processedContent = sectionContent
                      .replace(/\[Start Date\]/g, agreementData.startDate 
                        ? new Date(agreementData.startDate).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : "[Start Date]")
                      .replace(/\[Amount\]/g, agreementData.salary || "[Amount]")
                      .replace(/\[X\]/g, agreementData.probationPeriod || "[X]")
                      .replace(/\[Location\]/g, agreementData.workLocation || "[Location]")
                      .replace(/\[City \/ State\]/g, agreementData.workLocation || "[City / State]");

                    return (
                      <Box key={sectionKey}>
                        <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: "bold" }}>
                          {sectionKey}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: "justify", whiteSpace: "pre-line" }}>
                          {processedContent}
                        </Typography>
                      </Box>
                    );
                  })}
                
                {/* Disclaimers Section - Always added for API templates */}
                <Box sx={{ mt: 4, p: 1.5, backgroundColor: "#f9f9f9", borderRadius: 1, border: "1px solid #e0e0e0" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", color: "#d32f2f", fontSize: "0.75rem" }}>
                    IMPORTANT DISCLAIMERS
                  </Typography>
                  <Typography variant="caption" paragraph sx={{ textAlign: "justify", fontSize: "0.65rem", lineHeight: 1.4, display: "block" }}>
                    <strong>Entire Agreement:</strong> This Employment Agreement constitutes the entire agreement between the parties and supersedes all prior negotiations, representations, or agreements, whether written or oral. No terms or conditions other than those stated herein shall be binding unless made in writing and signed by both parties.
                  </Typography>
                  <Typography variant="caption" paragraph sx={{ textAlign: "justify", fontSize: "0.65rem", lineHeight: 1.4, display: "block" }}>
                    <strong>Modifications:</strong> Any modification, amendment, or waiver of any provision of this Agreement must be made in writing and signed by both the Employee and an authorized representative of the Company. No oral modifications or amendments shall be valid or binding.
                  </Typography>
                  <Typography variant="caption" paragraph sx={{ textAlign: "justify", fontSize: "0.65rem", lineHeight: 1.4, display: "block" }}>
                    <strong>Governing Law:</strong> This Agreement shall be governed by and construed in accordance with the laws of {agreementData.countryCode === "IN" ? "India" : "the applicable jurisdiction"}, without regard to its conflict of law provisions. Any disputes arising from this Agreement shall be subject to the exclusive jurisdiction of the courts in {agreementData.workLocation || "[Location]"}.
                  </Typography>
                  <Typography variant="caption" paragraph sx={{ textAlign: "justify", fontSize: "0.65rem", lineHeight: 1.4, display: "block" }}>
                    <strong>Severability:</strong> If any provision of this Agreement is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.
                  </Typography>
                  <Typography variant="caption" paragraph sx={{ textAlign: "justify", fontSize: "0.65rem", lineHeight: 1.4, display: "block" }}>
                    <strong>Employment Relationship:</strong> Nothing in this Agreement shall be construed as creating a partnership, joint venture, or agency relationship between the parties. The Employee acknowledges that employment is subject to satisfactory performance and compliance with company policies.
                  </Typography>
                  <Typography variant="caption" sx={{ textAlign: "justify", fontSize: "0.65rem", lineHeight: 1.4, display: "block" }}>
                    <strong>Policy Changes:</strong> The Company reserves the right to modify, amend, or terminate any company policies, procedures, or benefits at its discretion. The Employee will be notified of any material changes in accordance with applicable law.
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: "bold" }}>
                  1. POSITION AND DUTIES
                </Typography>
                <Typography variant="body1" paragraph sx={{ textAlign: "justify" }}>
                  The Employee is hereby appointed to the position of{" "}
                  <strong>{agreementData.position}</strong> in the{" "}
                  <strong>{agreementData.department}</strong> department. The Employee
                  shall report to <strong>{agreementData.reportingManager}</strong> and
                  shall perform all duties and responsibilities associated with this
                  position.
                </Typography>

                <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: "bold" }}>
                  2. COMMENCEMENT DATE
                </Typography>
                <Typography variant="body1" paragraph sx={{ textAlign: "justify" }}>
                  The employment shall commence on{" "}
                  <strong>
                    {agreementData.startDate
                      ? new Date(agreementData.startDate).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "TBD"}
                  </strong>
                  . This is a <strong>{agreementData.employmentType}</strong> position.
                </Typography>

                <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: "bold" }}>
                  3. PROBATION PERIOD
                </Typography>
                <Typography variant="body1" paragraph sx={{ textAlign: "justify" }}>
                  The Employee will be on probation for a period of{" "}
                  <strong>{agreementData.probationPeriod}</strong> from the date of
                  commencement. During this period, either party may terminate the
                  employment with one week's notice.
                </Typography>

                <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: "bold" }}>
                  4. COMPENSATION
                </Typography>
                <Typography variant="body1" paragraph sx={{ textAlign: "justify" }}>
                  The Employee shall receive a salary of{" "}
                  <strong>
                    {agreementData.salary || "[To be discussed]"}
                  </strong>
                  , payable in accordance with the Company's standard payroll practices.
                </Typography>

                <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: "bold" }}>
                  5. WORKING HOURS AND LOCATION
                </Typography>
                <Typography variant="body1" paragraph sx={{ textAlign: "justify" }}>
                  The normal working hours are <strong>{agreementData.workingHours}</strong>,
                  Monday through Friday, with a lunch break. The Employee's primary work
                  location will be at <strong>{agreementData.workLocation}</strong>.
                </Typography>

                <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: "bold" }}>
                  6. LEAVE ENTITLEMENTS
                </Typography>
                <Typography variant="body1" paragraph sx={{ textAlign: "justify" }}>
                  The Employee shall be entitled to the following leave per annum:
                </Typography>
                <Box sx={{ ml: 4, mb: 2 }}>
                  <Typography variant="body1">
                    • Annual Leave: <strong>{agreementData.annualLeave} days</strong>
                  </Typography>
                  <Typography variant="body1">
                    • Sick Leave: <strong>{agreementData.sickLeave} days</strong>
                  </Typography>
                  <Typography variant="body1">
                    • Emergency Leave: <strong>{agreementData.emergencyLeave} days</strong>
                  </Typography>
                </Box>

                <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: "bold" }}>
                  7. TERMINATION
                </Typography>
                <Typography variant="body1" paragraph sx={{ textAlign: "justify" }}>
                  After successful completion of the probation period, either party may
                  terminate this Agreement by providing{" "}
                  <strong>{agreementData.noticePeriod}</strong> written notice to the
                  other party.
                </Typography>

                <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: "bold" }}>
                  8. CONFIDENTIALITY
                </Typography>
                <Typography variant="body1" paragraph sx={{ textAlign: "justify" }}>
                  The Employee agrees to maintain strict confidentiality regarding all
                  company information, trade secrets, and proprietary data both during
                  and after employment.
                </Typography>

                <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: "bold" }}>
                  9. CODE OF CONDUCT
                </Typography>
                <Typography variant="body1" paragraph sx={{ textAlign: "justify" }}>
                  The Employee agrees to abide by all company policies, procedures, and
                  code of conduct as outlined in the Employee Handbook.
                </Typography>

                {/* Disclaimers Section - For default template */}
                <Box sx={{ mt: 4, p: 1.5, backgroundColor: "#f9f9f9", borderRadius: 1, border: "1px solid #e0e0e0" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", color: "#d32f2f", fontSize: "0.75rem" }}>
                    IMPORTANT DISCLAIMERS
                  </Typography>
                  <Typography variant="caption" paragraph sx={{ textAlign: "justify", fontSize: "0.65rem", lineHeight: 1.4, display: "block" }}>
                    <strong>Entire Agreement:</strong> This Employment Agreement constitutes the entire agreement between the parties and supersedes all prior negotiations, representations, or agreements, whether written or oral. No terms or conditions other than those stated herein shall be binding unless made in writing and signed by both parties.
                  </Typography>
                  <Typography variant="caption" paragraph sx={{ textAlign: "justify", fontSize: "0.65rem", lineHeight: 1.4, display: "block" }}>
                    <strong>Modifications:</strong> Any modification, amendment, or waiver of any provision of this Agreement must be made in writing and signed by both the Employee and an authorized representative of the Company. No oral modifications or amendments shall be valid or binding.
                  </Typography>
                  <Typography variant="caption" paragraph sx={{ textAlign: "justify", fontSize: "0.65rem", lineHeight: 1.4, display: "block" }}>
                    <strong>Governing Law:</strong> This Agreement shall be governed by and construed in accordance with the laws of {agreementData.countryCode === "IN" ? "India" : "the applicable jurisdiction"}, without regard to its conflict of law provisions. Any disputes arising from this Agreement shall be subject to the exclusive jurisdiction of the courts in {agreementData.workLocation || "[Location]"}.
                  </Typography>
                  <Typography variant="caption" paragraph sx={{ textAlign: "justify", fontSize: "0.65rem", lineHeight: 1.4, display: "block" }}>
                    <strong>Severability:</strong> If any provision of this Agreement is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.
                  </Typography>
                  <Typography variant="caption" paragraph sx={{ textAlign: "justify", fontSize: "0.65rem", lineHeight: 1.4, display: "block" }}>
                    <strong>Employment Relationship:</strong> Nothing in this Agreement shall be construed as creating a partnership, joint venture, or agency relationship between the parties. The Employee acknowledges that employment is subject to satisfactory performance and compliance with company policies.
                  </Typography>
                  <Typography variant="caption" sx={{ textAlign: "justify", fontSize: "0.65rem", lineHeight: 1.4, display: "block" }}>
                    <strong>Policy Changes:</strong> The Company reserves the right to modify, amend, or terminate any company policies, procedures, or benefits at its discretion. The Employee will be notified of any material changes in accordance with applicable law.
                  </Typography>
                </Box>
              </>
            )}

            <Box sx={{ mt: 6 }}>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Employee Signature:</strong>
                  </Typography>
                  <Box
                    sx={{
                      borderBottom: "1px solid #000",
                      height: "50px",
                      mb: 1,
                    }}
                  />
                  <Typography variant="body2">
                    Name: {agreementData.employeeName}
                  </Typography>
                  <Typography variant="body2">Date: _______________</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Employer Signature:</strong>
                  </Typography>
                  <Box
                    sx={{
                      borderBottom: "1px solid #000",
                      height: "50px",
                      mb: 1,
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "center",
                      fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                      fontSize: "1.8rem",
                      color: "#1a1a1a",
                      fontStyle: "italic",
                      fontWeight: "500",
                      letterSpacing: "0.05em",
                      transform: "rotate(-2deg) skewX(-5deg)",
                      textShadow: "1px 1px 0px rgba(0,0,0,0.1)",
                    }}
                  >
                    {generateSignatureStyle(agreementData.employerName)}
                  </Box>
                  <Typography variant="body2">
                    Name: {agreementData.employerName}
                  </Typography>
                  <Typography variant="body2">
                    Designation: {agreementData.employerDesignation}
                  </Typography>
                  <Typography variant="body2">
                    Date: {agreementData.employerSignatureDate 
                      ? new Date(agreementData.employerSignatureDate).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : new Date().toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                    }
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                borderTop: "2px solid #e0e0e0",
                mt: 4,
                pt: 2,
                px: 4,
                pb: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f9f9f9",
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ display: "block", fontWeight: "bold" }}>
                  {agreementData.companyName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                  {agreementData.companyAddress}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.7rem" }}>
                  Employment Agreement
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                  Employee ID: {agreementData.employeeId}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          onClick={toggleEdit}
          variant="outlined"
          startIcon={isEditing ? <PreviewIcon /> : <EditIcon />}
        >
          {isEditing ? "Preview" : "Edit"}
        </Button>
        <Button
          onClick={handleGeneratePDF}
          variant="contained"
          startIcon={<PictureAsPdfIcon />}
          sx={{
            backgroundColor: "#1976d2",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          Generate PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmploymentAgreementGenerator;
